/* eslint-disable func-names */
/* global BackboneEx _ */
/**
 * Settings.
 */
import React from 'react';
import ReactDOM from 'react-dom';
import { enableRecording } from 'common/analytics';
import { NavigationAnalytics, L2Page, L3Page, mapPageToL3Enum } from 'common/tracking/navigation';
import URI from 'urijs';

import Wahanda from 'common/wahanda';
import { Reconnect } from 'federation/Reconnect';
import { openChat } from 'utilities/support-chat';
import App from 'common/backbone-app';
import * as localesConfig from 'config/domains-locales';
import _ from 'underscore';

const VENUE_SETTING_NAV = '#venue-settings';
const VENUE_SETTING_NAV_TABS = `${VENUE_SETTING_NAV}-tabs`;
const OPENING_HOURS_TAB = '#opening-hours';
const BANK_DETAILS_CONTAINER = '#bank-details';
const NOTIFICATION_FORM_CONTAINER = '#notification-form';

const SHOW_NOTIFICATION_FORM_FEATURE = 'THY-2000-prevent-competitor-domains';
const SHOW_NEW_BANK_DETAILS_PAGE_FEATURE = 'CD-1415-new-Bank-Details-page';
const SHOW_NEW_FINANCE_SETTINGS_PAGE_FEATURE = 'CD-1277-finance-settings-page';
const SHOW_NEW_DIRECT_DEBIT_FLOW = 'TEF-1815-direct-debit-enable-modal';
const ENABLE_STRIPE_DIRECT_DEBIT = 'TEF-1974-enable-stripe-direct-debit-by-supplier';
const SHOW_BACS_DIRECT_DEBIT = 'TEF-2264-BACS-debit-support';
const SHOW_ONLINE_BOOKING_PAGES_FEATURE = 'THY-2175-online-booking-pages';
const ONLINE_BOOKING_SUBTAB_IDS = [
  'online-booking-widget',
  'online-booking-website',
  'online-booking-settings',
];

App.Views.Settings = BackboneEx.View.Main.extend(
  {
    /**
     * @var App.Views.Form
     */
    currentFormView: null,
    venueModel: null,
    suppliermodel: null,
    micrositesModel: null,
    /**
     * Hash of formViews.
     */
    formViews: {},

    initialize: function initialize() {
      enableRecording();

      this.$('#google-maps-scripts').html(
        Wahanda.Template.renderTemplate('google-maps-api', {
          key: App.config.getGoogleMapApiKey(),
        }),
      );

      const venueModel = new App.Models.Venue({
        venueId: null,
        relations: ['opening-hours', 'images', 'fulfillment-communication', 'phones'],
      });
      this.venueModel = venueModel;

      const micrositesModel = new App.Models.Microsite();
      this.micrositesModel = micrositesModel;

      const catCollection = new App.Collections.EmployeeCategories();
      catCollection.nameComparatorEnabled(false);

      const supplierModel = new App.Models.Supplier({
        id: null,
      });
      this.supplierModel = supplierModel;

      this.formViews['venue-details'] = new App.Views.Settings.VenueDetails({
        el: this.$('#venue-details'),
        model: venueModel,
      });
      this.formViews['opening-hours'] = new App.Views.Settings.OpeningHours({
        el: this.$(OPENING_HOURS_TAB),
        model: venueModel,
      });
      this.formViews.resources = new App.Views.Settings.VenueResources({
        el: this.$('#resources'),
        model: venueModel,
      });
      this.formViews.pos = new App.Views.Settings.Pos({
        el: this.$('#pos'),
        model: venueModel,
      });
      this.formViews.payments = new App.Views.Settings.SupplierPayments({
        el: this.$('#payments'),
        model: supplierModel,
      });

      this.formViews['billing-details'] = new App.Views.Settings.SupplierSettings({
        el: this.$('#billing-details'),
        model: supplierModel,
      });
      this.formViews['bank-details'] = new App.Views.Settings.SupplierBankDetails({
        el: this.$('#bank-details'),
        model: supplierModel,
      });
      // When the Reconnect notification form is enabled it owns the fulfillment
      // tab, so the legacy form view is not initialised (see renderNotificationForm).
      if (!Wahanda.Features.isEnabled(SHOW_NOTIFICATION_FORM_FEATURE)) {
        this.formViews.fulfillment = new App.Views.Settings.NotificationsSettings({
          el: this.$('#fulfillment'),
          model: venueModel,
        });
      }
      // When the Reconnect online-booking shell is enabled it owns the whole
      // section (all three tabs), so none of the legacy form views are
      // initialised (see renderOnlineBookingPages).
      if (!Wahanda.Features.isEnabled(SHOW_ONLINE_BOOKING_PAGES_FEATURE)) {
        this.formViews['online-booking-settings'] = new App.Views.Settings.OnlineBookingSettings({
          el: this.$('#online-booking-settings'),
          model: venueModel,
        });
        this.formViews['online-booking-integrations'] = new App.Views.Settings.Integrations({
          el: this.$('#online-booking-integrations .data-content'),
          model: venueModel,
        });
        this.formViews['online-booking-widget'] = new App.Views.Settings.OnlineBookingWidget({
          el: this.$('#online-booking-widget'),
          model: venueModel,
          micrositesModel,
        });
      } else {
        // The Reconnect Settings tab's Lead time row can't call openChat()
        // directly (Reconnect can't import Connect code), so it dispatches
        // this event instead — see reconnect's useOpenSupportChat.ts.
        window.addEventListener('reconnect:open-support-chat', () =>
          openChat('online booking settings'),
        );
      }
      // When the Reconnect online-booking shell owns the section, the legacy
      // Integrations subtab is gone (see above); its wizard is relocated to
      // this new top-level tab instead (see setTabVisibilityByConfig).
      if (Wahanda.Features.isEnabled(SHOW_ONLINE_BOOKING_PAGES_FEATURE)) {
        this.formViews['external-calendars-overview'] = new App.Views.Settings.Integrations({
          el: this.$('#external-calendars-overview'),
          model: venueModel,
          headerTitle: Wahanda.lang.settings.externalCalendars.title,
        });
      }

      const self = this;

      App.on('app:loaded', () => {
        const venueId = App.config.get('venue').id;
        const supplierId = App.config.get('venue').supplierId;

        const modelFetchOptions = {
          success: function success(model) {
            model.trigger('fetched');
          },
        };

        micrositesModel.clear({ silent: true });
        micrositesModel.fetch(modelFetchOptions);

        venueModel.set('venueId', venueId).fetch(modelFetchOptions);
        if (supplierId > 0) {
          supplierModel.set('id', supplierId).fetch(modelFetchOptions);
        }

        catCollection.setVenueId(venueId);
      });

      App.on(Wahanda.Event.APP_VENUE_CHANGED, () => {
        if (self.selectedSubviewTabId() === '#settings') {
          self.renderFinanceSettings();
        }
      });

      // Stop navigation into another module, if the current form has changes
      App.headerView.on('change-module', (cancelCallback) => {
        if (!self.canChangeTab()) {
          cancelCallback();
        }
      });

      // Setting the default module & tab
      let options = this.options.options;
      if (!options || (options && !options.initialModule && !options.initialTab)) {
        options = _.extend(options || {}, {
          initialModule: 'venue-settings',
          initialTab: 'venue-details',
          initialIgu: '',
        });
        this.options.options = options;
      }
      this.currentFormView = this.formViews[options.initialTab] || null;

      // add Iglu bits
      const $igluTabLink = $('#iglu-settings-links');
      if (App.isIgluEnabled()) {
        $igluTabLink.show();
        $('#iglu-settings-venue').show();
        // Don't trigger any tab functionality
        $('#iglu-supplier-link').click((event) => {
          event.stopPropagation();
          App.Views.Dialog.IgluModal.openIgluSupplierSettings(App.config.get('venue').supplierId);
        });
        $('#iglu-venue-link').click((event) => {
          event.stopPropagation();
          App.Views.Dialog.IgluModal.openIgluVenueSettings(App.config.get('venue').id);
        });
      } else {
        // Not masq`d in, won't be anytime in this login session. Remove the tab.
        $igluTabLink.remove();
        $('#iglu-settings-venue').remove();
      }

      this.rendered = false;
    },

    /**
     * Renders the whole settings view.
     */
    render: function render() {
      if (!Wahanda.Permissions.accessSettings()) {
        window.location = Wahanda.Url.getFullUrl('calendar');
        return;
      }

      App.headerView.setModule('settings');
      const self = this;
      const options = this.options.options;

      this.mainTabs = this.$el.wShow().tabs({
        // follow tab hash tags in URL
        select: function select(event, ui) {
          if (self.canChangeTab() && ui.tab.hash !== '#void') {
            if (self.selectedSubviewTabId() === BANK_DETAILS_CONTAINER) {
              self.renderBankDetails();
            }
            if (self.selectedSubviewTabId() === 'settings') {
              self.renderFinanceSettings();
            }

            const availableTabHash = self.availableSubviewTabId(ui.tab.hash);
            const availableTab = availableTabHash && availableTabHash.replace('#', '');
            const innerTab = availableTab || '';
            const venueUrl = `${App.config.currentVenueHash()}/${ui.tab.hash.replace('#', '')}/`;
            let tabUrl = venueUrl;

            if (self.selectedSubviewTabId() === availableTabHash) {
              // Refetch the inner view model contents, if any
              self.onSubviewChange(innerTab);
              tabUrl = `${venueUrl}${innerTab || ''}`;
            } else if (innerTab) {
              // Allowed and selected tabs do not match. Select the allowed one.
              self.$(`${ui.tab.hash}-tabs`).tabs('select', availableTabHash);
              self.onSubviewChange(availableTab);
              tabUrl = `${venueUrl}${availableTab || ''}`;
            }
            let igluEndpoint = '';
            if (App.mainViewOptions != null && App.mainViewOptions.initialIglu) {
              // the initialIglu value should be missing /_,
              // in order to rebuild the url we add it back in here.
              igluEndpoint = `/_${App.mainViewOptions.initialIglu}`;
            }
            // stop iglu from being added to all tab changes
            if (window.location.hash.indexOf(igluEndpoint) === -1) {
              igluEndpoint = '';
            }
            App.mainRouter.navigate(tabUrl + igluEndpoint);
            // track page event on each settings main tab change
            self.rendered && Wahanda.Tracking.page();
            return true;
          }
          return false;
        },
      });

      this.$(VENUE_SETTING_NAV_TABS)
        .add('#finance-tabs')
        .add('#notifications-settings-tabs')
        .add('#online-booking-tabs')
        .tabs({
          // follow tab hash tags in URL
          select: function select(event, ui) {
            if (self.canChangeTab()) {
              const subviewHash = ui.tab.hash.replace('#', '');
              const subPage = mapPageToL3Enum(subviewHash);
              self.onSubviewChange(subviewHash);
              App.mainRouter.navigate(
                `${App.config.currentVenueHash()}/${self
                  .selectedMainTabId()
                  .replace('#', '')}/${subviewHash}`,
              );
              NavigationAnalytics.trackPageEventView(subPage);
              // track page event on each settings sub-tab change
              self.rendered && Wahanda.Tracking.page();
              return true;
            }
            return false;
          },
        });

      // payNow is carried by the monthly invoice emails; showPayNow by the debt
      // campaign. Native new URL not supported for IE11
      const windowUrl = new URI(window.location.href);
      const settingsQuery = windowUrl.query(true);
      if (settingsQuery.payNow || settingsQuery.showPayNow) {
        this.mainTabs.tabs('select', '#finance');
      } else if (options && options.initialModule) {
        this.mainTabs.tabs('select', `#${options.initialModule}`);
        options.initialModule = null;
      }
      if (options && options.initialTab) {
        this.$(`${this.selectedMainTabId()}-tabs`).tabs('select', `#${options.initialTab}`);
        const initialFirstTabs = [
          L3Page.VENUE_DETAILS,
          'payments',
          L3Page.FULFILLMENT,
          'online-booking-widget',
        ];
        if (initialFirstTabs.includes(options.initialTab)) {
          NavigationAnalytics.trackPageEventView(mapPageToL3Enum(options.initialTab));
        }
        options.initialTab = null;
      }
      App.Views.Dialog.IgluModal.validateAndShow(options.initialIglu);

      this.setTabVisibility();

      this.renderSidebarInfo();
      document.body.classList.add('responsive-balance');
      this.renderMobileNav();
      this.listenTo(App, Wahanda.Event.SETTINGS_NAVIGATE_TAB, (data) => {
        if (data.tabId) {
          this.mainTabs.tabs('select', `#${data.tabId}`);
        }
        if (data.subTabId) {
          this.$(`${this.selectedMainTabId()}-tabs`).tabs('select', `#${data.subTabId}`);
          this.onSubviewChange(data.subTabId);
        }
      });

      App.setUpGoLive();

      this.rendered = true;
    },

    renderSidebarInfo() {
      const sidebarInfoNode = this.$('#sidebar-info').get(0);
      App.ES6.Initializers.SidebarInfo({
        node: sidebarInfoNode,
        liveChatSource: 'settings sidebar',
      }).render();
    },

    renderMobileNav() {
      const node = document.getElementById('mobile-nav-mount');
      const langHeader = Wahanda.lang.header;
      const langSettings = Wahanda.lang.settings;
      const APP_NAV = [
        { href: '/calendar', label: langHeader.calendar },
        { href: '/dashboard', label: langHeader.dashboard },
        { href: '/menu', label: langHeader.menu },
        { href: '/team', label: langHeader.team },
        { href: '/products', label: langHeader.products },
        { href: '/clients', label: langHeader.clients },
        { href: '/marketing', label: langHeader.marketing },
        { href: '/reports', label: langHeader.reports },
      ];
      const SETTINGS_TABS = [
        { id: 'venue-settings', label: langSettings.venue.title },
        {
          id: 'finance',
          label: langSettings.supplier.title,
          subTabs: [
            { id: 'payments', label: langSettings.supplier.payments.title },
            { id: 'settings', label: langSettings.finance.title },
            { id: 'balance', label: langSettings.supplier.balance.title },
          ],
        },
        { id: 'notifications-settings', label: langSettings.notifications.title },
        { id: 'online-booking', label: langSettings.onlineBooking.label },
        ...(Wahanda.Features.isEnabled(SHOW_ONLINE_BOOKING_PAGES_FEATURE)
          ? [{ id: 'external-calendars', label: langSettings.externalCalendars.title }]
          : []),
      ];
      if (!node) return;

      const LANG_CONTACTS = Wahanda.lang.home.contacts;
      const venue = App.config.get('venue');
      const account = App.config.get('account');
      const domain = localesConfig.DOMAIN_TO_LOCALE_MAP_INVERTED[App.getClientLocale()];
      const referUrl = new URL(LANG_CONTACTS.referSalonUrl, `https://www.${domain}`);
      referUrl.searchParams.append('venue_id', venue.id);
      referUrl.searchParams.append(
        'venue_name',
        App.config.accountEmployeeName() || App.config.accountFullName(),
      );
      referUrl.searchParams.append('referrer_email', account.emailAddress);
      referUrl.searchParams.append('referrer_id', account.id);
      referUrl.searchParams.append('referrer_type', 'connect');

      const FOOTER_LINKS = [
        { label: LANG_CONTACTS.referSalon, href: referUrl.href },
        { label: LANG_CONTACTS.helpCenter, href: LANG_CONTACTS.helpCenterUrl },
        { label: LANG_CONTACTS.getHelpButton, onClick: () => openChat('settings sidebar') },
      ];

      ReactDOM.render(
        <Reconnect
          name="MobileNav"
          props={{
            appNav: APP_NAV,
            settingsTabs: SETTINGS_TABS,
            footerLinks: FOOTER_LINKS,
          }}
        />,
        node,
      );
    },

    renderBankDetails() {
      if (Wahanda.Features.isEnabled(SHOW_NEW_BANK_DETAILS_PAGE_FEATURE)) {
        const node = this.$(BANK_DETAILS_CONTAINER).get(0);
        ReactDOM.render(
          <Reconnect
            name="BankDetailsPage"
            props={{
              notifyFn: Wahanda.ToastNotifications.sendMessage,
              hasNewDirectDebitFlow: Wahanda.Features.isEnabled(SHOW_NEW_DIRECT_DEBIT_FLOW),
            }}
          />,
          node,
        );
      }
    },

    renderFinanceSettings() {
      if (Wahanda.Features.isEnabled(SHOW_NEW_FINANCE_SETTINGS_PAGE_FEATURE)) {
        const node = this.$('div[data-finance-settings-tab]').get(0);
        ReactDOM.render(
          <Reconnect
            name="FinanceSettingsPage"
            props={{
              notifyFn: Wahanda.ToastNotifications.sendMessage,
              hasNewDirectDebitFlow:
                Wahanda.Features.isEnabled(SHOW_NEW_DIRECT_DEBIT_FLOW) ||
                Wahanda.Features.isEnabled(ENABLE_STRIPE_DIRECT_DEBIT),
              hasBacsDirectDebit: Wahanda.Features.isEnabled(SHOW_BACS_DIRECT_DEBIT),
            }}
          />,
          node,
        );
      }
    },

    renderNotificationForm() {
      if (Wahanda.Features.isEnabled(SHOW_NOTIFICATION_FORM_FEATURE)) {
        const node = this.$(NOTIFICATION_FORM_CONTAINER).get(0);
        if (!node) {
          return;
        }
        // The legacy form view is not initialised under this flag; hide its markup.
        this.$('#legacy-fulfillment-form').hide();
        // Only one tab remains; the Reconnect form renders its own heading, so
        // drop the now-redundant inner tab bar and collapse the tab-content
        // offset that reserved space for it.
        this.$('#notifications-settings-tabs > .nav3').hide();
        this.$('#fulfillment').addClass('is-single-tab');
        ReactDOM.render(
          <Reconnect
            name="NotificationForm"
            props={{
              notifyFn: Wahanda.ToastNotifications.sendMessage,
              onSaved: () => App.trigger(Wahanda.Event.SETTINGS_FULFILLMENT_COMMUNICATION_SAVED),
            }}
          />,
          node,
        );
      }
    },

    renderBalance() {
      const node = this.$('#balance').get(0);
      ReactDOM.render(<Reconnect name="BalanceOverview" />, node);
    },

    renderOnlineBookingPages() {
      if (Wahanda.Features.isEnabled(SHOW_ONLINE_BOOKING_PAGES_FEATURE)) {
        // Mounts one level up from the legacy widget tab-content div: Reconnect
        // now owns the whole section (header + tab bar + all three tabs), so
        // rendering into the outer container replaces the legacy
        // #online-booking-tabs nav + tab-content markup entirely.
        const node = this.$('#online-booking').get(0);
        if (!node) {
          return;
        }
        ReactDOM.render(
          <Reconnect
            name="OnlineBookingPage"
            props={{
              testID: 'online-booking-page',
            }}
          />,
          node,
        );
      }
    },

    /**
     * Returns the currently selected tab`s ID hash.
     *
     * @return string
     */
    selectedMainTabId: function selectedMainTabId() {
      const curTab = this.mainTabs.find('#nav2 .ui-tabs-active a')[0];
      return curTab.hash;
    },

    /**
     * Returns the main view`s selected inner tab, if any.
     * @param string mainViewId OPTIONAL Main view to use,
     * in format #view-id. If none given, takes current.
     * @return string | null
     */
    selectedSubviewTabId: function selectedSubviewTabId(mainViewId) {
      const mainView = $(mainViewId || this.selectedMainTabId());
      const tab = mainView.find('.ui-tabs-nav:first .ui-tabs-active a')[0];
      return tab ? tab.hash : null;
    },

    availableSubviewTabId: function availableSubviewTabId(mainViewId) {
      if (mainViewId === '#online-booking') {
        // Respect a deep-linked sub-tab (e.g. a page refresh landing on
        // .../online-booking/online-booking-settings) instead of always
        // forcing the widget tab, which used to overwrite the URL hash
        // before Reconnect's OnlineBookingPage ever got a chance to read it.
        const requestedTab = this.options.options && this.options.options.initialTab;
        if (ONLINE_BOOKING_SUBTAB_IDS.includes(requestedTab)) {
          return `#${requestedTab}`;
        }
        return '#online-booking-widget';
      }
      if (mainViewId === '#external-calendars') {
        return '#external-calendars-overview';
      }
      const mainView = $(mainViewId || this.selectedMainTabId());
      const $tabs = mainView.find('.ui-tabs-nav:first li');
      let $tab = $tabs.filter('.ui-tabs-active').not('.hidden');
      if (!$tab.length) {
        $tab = $tabs.not('.hidden').eq(0);
      }
      const tab = $tab.find('a')[0];

      return tab ? tab.hash : null;
    },

    /**
     * Can the user safely change the tab?
     *
     * @return boolean
     */
    canChangeTab: function canChangeTab() {
      if (this.rendered) {
        const view = this.currentFormView;
        return view && view.canNavigateAway ? view.canNavigateAway() : true;
      }
      return true;
    },

    /**
     * Handles changing the subview.
     *
     * @param string subviewHash
     */
    onSubviewChange: function onSubviewChange(subviewHash) {
      if (this.currentFormView) {
        // Trigger the "hidden" event for the view to perform any needed cleanup operations
        this.currentFormView.trigger('hidden');
      }
      this.currentFormView = this.formViews[subviewHash];
      if (
        this.currentFormView &&
        this.currentFormView.model &&
        (this.currentFormView.model.id ||
          (this.venueModel === this.currentFormView.model && this.venueModel.id))
      ) {
        // Refetch the model to stay up-to-date. Fetch only if it has ID defined.
        this.currentFormView.model.fetch({
          success: function success(model) {
            model.trigger('fetched');
          },
        });
      }
      if (this.currentFormView) {
        this.currentFormView.trigger('visible');
      }

      // TODO: tracking?
      switch (subviewHash) {
        case 'bank-details':
          // landing on the Bank Details tab
          this.renderBankDetails();
          break;
        case 'settings':
          this.renderFinanceSettings();
          break;
        case 'balance':
          this.renderBalance();
          break;
        case 'fulfillment':
          this.renderNotificationForm();
          break;
        case 'online-booking-widget':
        case 'online-booking-website':
        case 'online-booking-settings':
        case 'online-booking-integrations':
          this.renderOnlineBookingPages();
          break;
      }
    },

    addSidebarClickTracking() {
      const PAGE_TABS = {
        '#venue-settings-tab': L2Page.VENUE_DETAILS,
        '#finance-tab': L2Page.FINANCE,
        '#notifications-settings-tab': L2Page.NOTIFICATIONS,
        '#online-booking-tab': L2Page.ONLINE_BOOKING,
        '#external-calendars-tab': L2Page.EXTERNAL_CALENDARS,
      };
      const PAGE_SUB_TABS = {
        [L2Page.VENUE_DETAILS]: L3Page.VENUE_DETAILS,
        [L2Page.FINANCE]: L3Page.PAYMENTS,
        [L2Page.NOTIFICATIONS]: L3Page.FULFILLMENT,
        [L2Page.ONLINE_BOOKING]: L3Page.ONLINE_BOOKING_WIDGET,
        [L2Page.EXTERNAL_CALENDARS]: L3Page.EXTERNAL_CALENDARS,
      };

      const namespace = '.settingsViewTracking';

      Object.keys(PAGE_TABS).forEach((tab) => {
        this.$(tab)
          .off(namespace)
          .on(`click${namespace}`, () => {
            NavigationAnalytics.trackPageEventView(PAGE_SUB_TABS[PAGE_TABS[tab]]);
            NavigationAnalytics.trackSidebarClick(PAGE_TABS[tab]);
          });
      });
    },

    addPageTabClickTracking() {
      const PAGE_TABS = {
        // Venue
        '#venue-settings-subtabs-details': L3Page.VENUE_DETAILS,
        '#venue-settings-subtabs-opening-hours': L3Page.OPENING_HOURS,
        '#resources-tab': L3Page.RESOURCES,
        // Finance
        '#finance-subtab-payments': L3Page.PAYMENTS,
        '#finance-subtab-billing-details': L3Page.BILLING_DETAILS,
        '#finance-subtab-bank-details': L3Page.BANK_DETAILS,
        '#finance-subtab-pos': L3Page.POS,
        '#finance-subtab-settings': L3Page.SETTINGS,
        '#finance-subtab-balance': L3Page.BALANCE,
        // Notifications
        '#notifications-fulfillment-link': L3Page.FULFILLMENT,
        // Online booking
        '#online-booking-subtab-widget': L3Page.ONLINE_BOOKING_WIDGET,
        '#online-booking-subtab-settings': L3Page.ONLINE_BOOKING_SETTINGS,
        '#online-booking-subtab-integrations': L3Page.ONLINE_BOOKING_INTEGRATIONS,
      };

      const namespace = '.settingsTabsTracking';

      Object.keys(PAGE_TABS).forEach((tab) => {
        this.$(tab)
          .off(namespace)
          .on(`click${namespace}`, () => {
            NavigationAnalytics.trackPageTabClick(PAGE_TABS[tab]);
          });
      });
    },

    /**
     * Showing/hiding tabs based on permissions.
     *
     * Please use the "hidden" class for hiding as this is required in other places.
     */
    setTabVisibility: function setTabVisibility() {
      const $allTabs = this.$('#nav2').children();
      const $allSubTabs = this.$('.nav3').find('li');

      const currentPage = mapPageToL3Enum(this.selectedSubviewTabId().replace('#', ''));
      NavigationAnalytics.trackPageView(currentPage);

      // Show all possibly previously hidden elements
      $allTabs.add($allSubTabs).wShow();
      this.$el.wShow();

      if (Wahanda.Permissions.manageVenueDetails()) {
        // eslint-disable-next-line
        this.setTabVisibilityByConfig();
      } else {
        let $toHide = $allTabs;
        // No permissions for viewing/editing.
        // Check for separate permissions and show tabs accordingly.
        if (Wahanda.Permissions.viewFinanceData()) {
          $toHide = $toHide
            .not('#finance-tab')
            // Hide all subtabs of finance, except for remittances
            .add(this.$('#finance-tabs').find('.nav3').find('li').not('#finance-subtab-payments'));
        }
        // Hide tabs to which the user does not have any permissions
        $toHide.wHide();

        if ($allTabs.filter(':visible').length === 0) {
          // No tabs visible. Hide the whole settings view.
          this.$el.wHide();
        } else {
          // Select first active tab. And subtab.
          const $firstTab = $allTabs.filter(':visible').eq(0);
          this.mainTabs.tabs('select', $firstTab.find('a').attr('href'));
        }
      }
      this.addSidebarClickTracking();
      this.addPageTabClickTracking();
    },

    setTabVisibilityByConfig: function setTabVisibilityByConfig() {
      const showFinancesTab = Wahanda.Permissions.viewFinanceData();
      this.$('#finance-tab').toggleClass('hidden', !showFinancesTab);

      if (!showFinancesTab && this.selectedMainTabId() === '#finance') {
        this.mainTabs.tabs('select', VENUE_SETTING_NAV);
      }

      // Integrations settings tab
      const integrationsPermitted = App.isAdministrator() || App.isMasquerading();
      this.$('#online-booking-subtab-integrations').wToggle(integrationsPermitted);

      // External calendars tab: relocated Integrations wizard, only shown
      // once the Reconnect online-booking shell has taken over the section.
      const externalCalendarsPermitted =
        Wahanda.Features.isEnabled(SHOW_ONLINE_BOOKING_PAGES_FEATURE) && integrationsPermitted;
      this.$('#external-calendars-tab').wToggle(externalCalendarsPermitted);

      // Dated tabs: DEPRECATED
      const sellsDated = App.config.canSellDated();
      this.$('#room-types-tab').wToggle(sellsDated);
      if (!sellsDated && this.selectedSubviewTabId() === '#room-types') {
        this.$(`${VENUE_SETTING_NAV}-tabs`).tabs('select', '#venue-details');
      }

      const posEnabled = App.config.get('venue').pointOfSaleEnabled;
      this.$('#finance-subtab-pos').wToggle(posEnabled);
      if ((!posEnabled || !showFinancesTab) && this.selectedSubviewTabId() === '#pos') {
        this.$('#finance-tabs').tabs('select', '#billing-details');
      }

      const giftVouchers = App.isFeatureSupported('wahanda-voucher');
      this.$('#venue-vouchers-tab').wToggle(giftVouchers);
      if (!giftVouchers && this.selectedSubviewTabId() === '#venue-vouchers') {
        this.$(VENUE_SETTING_NAV_TABS).tabs('select', '#venue-details');
      }

      // manage tabs based on new finance settings page feature flag
      if (Wahanda.Features.isEnabled(SHOW_NEW_FINANCE_SETTINGS_PAGE_FEATURE)) {
        const migratedTabs = ['billing-details', 'bank-details', 'pos'];
        migratedTabs.forEach((tab) => this.$(`#finance-subtab-${tab}`).wToggle(false));

        const currentSubTab = this.selectedSubviewTabId().replace('#', '');
        const isTryingToShowOldSubTab = migratedTabs.some((tab) => tab === currentSubTab);

        if (isTryingToShowOldSubTab) {
          this.$('#finance-tabs').tabs('select', '#settings');
        }
      } else {
        // hide settings tab
        this.$('#finance-subtab-settings').wToggle(false);
      }
    },

    isSubviewActive: function isSubviewActive(view) {
      return this.currentFormView === view;
    },
  },
  {
    renderPreviewLink: function renderPreviewLink($link, venueModel) {
      const canShowPreviewLink = App.isFeatureSupported('marketplace');
      const previewLink = canShowPreviewLink && venueModel.getLinkByType('O');

      if (previewLink) {
        $link.attr('href', previewLink.uri);
      }
      $link.wToggle(!!previewLink);
    },
  },
);

App.Views.Settings.Dynamic = {};
